import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaEdit, FaArrowLeft } from 'react-icons/fa';
import { Button } from '../../../components/ui';
import apiService from '../../../services/apiService';

const breadcrumb = (
  <span>
    Home &nbsp;/&nbsp; Webber &nbsp;/&nbsp; 
    <a href="/web-pages" className="text-blue-600">Webpages</a> &nbsp;/&nbsp; 
    <span className="text-gray-700">View Webpage</span>
  </span>
);

const ViewWebPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [webpage, setWebpage] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    const fetchWebpage = async () => {
      try {
        const data = await apiService.request(`/i/ims/frontpage/${id}?populate=website&select_website=title`);
        setWebpage(data);
      } catch (error) {
        console.error('Error fetching webpage:', error);
        alert('Failed to load webpage');
      } finally {
        setLoading(false);
      }
    };

    fetchWebpage();
  }, [id]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!webpage) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error! </strong>
          <span className="block sm:inline">Webpage not found or you don't have permission to view it.</span>
        </div>
      </div>
    );
  }


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="p-4">
      <div className="flex items-center text-sm mb-6">
        {breadcrumb}
      </div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">View Webpage</h1>
        <div className="flex space-x-2">
          <Button
            onClick={() => navigate(`/web-pages/edit/${id}`)}
            variant="warning"
            size="sm"
            icon={FaEdit}
          >
            Edit
          </Button>
          <Button
            onClick={() => navigate('/web-pages')}
            variant="light"
            size="sm"
            icon={FaArrowLeft}
          >
            Back to List
          </Button>
        </div>
      </div>



      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="border-b">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'details'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('meta')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'meta'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Meta Information
            </button>
            <button
              onClick={() => setActiveTab('scripts')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'scripts'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Meta Information
            </button>
            {webpage.customParams && Object.keys(webpage.customParams).length > 0 && (
              <button
                onClick={() => setActiveTab('params')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'params'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Additional Details
              </button>
            )}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Title</h3>
                  <p className="mt-1 text-gray-900">{webpage.title || '-'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Website</h3>
                  <p className="mt-1 text-gray-900">{webpage.website?.title || '-'}</p>
                </div>
                {/* <div>
                  <h3 className="text-sm font-medium text-gray-500">View File</h3>
                  <p className="mt-1 text-gray-900">{webpage.viewFile || 'default.ejs'}</p>
                </div> */}
                
                <div className="md:col-span-2">
                  <h3 className="text-sm font-medium text-gray-500">Description</h3>
                  <p className="mt-1 text-gray-900 whitespace-pre-line">
                    {webpage.description || 'No description provided.'}
                  </p>
                </div>
                {webpage.featureImg && (
                  <div className="md:col-span-2">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Featured Image</h3>
                    <div className="mt-1">
                      <div className="h-40 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
                        {webpage.featureImg ? (
                          <img 
                            src={webpage.featureImg} 
                            alt="Featured" 
                            className="h-full w-auto max-w-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              const fallback = e.target.parentNode.querySelector('.image-fallback');
                              if (fallback) {
                                fallback.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div className="image-fallback hidden w-full h-full items-center justify-center bg-gray-200 text-gray-500">
                          Image not available
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Status</h3>
                  <p className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      webpage.status 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {webpage.status ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-medium text-gray-500">Additional Information</h3>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Created At</p>
                    <p className="text-gray-900">{formatDate(webpage.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Updated At</p>
                    <p className="text-gray-900">{formatDate(webpage.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'meta' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                {Object.entries(webpage.metaTags || {}).map(([key, value]) => {
                  if (!value) return null;
                  
                  // Special handling for ogImage
                  if (key === 'ogImage') {
                    return (
                      <div key={key} className="break-words">
                        <h3 className="text-sm font-medium text-gray-500 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </h3>
                        <div className="mt-1">
                          <div className="h-40 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
                            {value ? (
                              <img 
                                src={value} 
                                alt="OG Image"
                                className="h-full w-auto max-w-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.style.display = 'none';
                                  const fallback = e.target.parentNode.querySelector('.image-fallback');
                                  if (fallback) {
                                    fallback.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null}
                            <div className="image-fallback hidden w-full h-full items-center justify-center bg-gray-200 text-gray-500">
                              Image not available
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  // Default rendering for other meta tags
                  return (
                    <div key={key} className="break-words">
                      <h3 className="text-sm font-medium text-gray-500 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </h3>
                      <p className="mt-1 text-gray-900 whitespace-pre-line">
                        {value || '-'}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {activeTab === 'scripts' && webpage.scripts && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Scripts</h3>
                  <p className="mt-1 text-gray-900 whitespace-pre-line">
                    {webpage.scripts || '-'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'params' && webpage.customParams && (
            <div className="overflow-hidden border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Key
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(webpage.customParams).map(([key, value]) => (
                    <tr key={key} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {key}
                      </td>
                      <td className="px-6 py-4 whitespace-pre-wrap text-sm text-gray-500 break-all">
                        {String(value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewWebPage;
